import React, { useState } from 'react';
import { Alert, Box, Chip, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';

import TrendOpportunityCard from './components/TrendOpportunityCard';
import TrendOpportunityDetailDialog from './components/TrendOpportunityDetailDialog';
import TrendCardSkeletonGrid from './components/TrendCardSkeleton';
import { useTrendsOpportunities } from './hooks/useTrendsOpportunities';
import type { TrendOpportunity } from './types';
import { isLatestRunPresentation, sortByRunPresentationThenDate } from './runPresentationUtils';
import { matchesTrendChannel, useTrendChannel, TrendChannelFilter } from './channelFilter';
import { PAGE_HEADING_SX, TREND_COLORS } from './trendTheme';

export default function TrendsOpportunitiesPage() {
  const { channel } = useTrendChannel();
  const { opportunities, loading, error } = useTrendsOpportunities();
  const [selectedOpportunity, setSelectedOpportunity] = useState<TrendOpportunity | null>(null);
  const [search, setSearch] = useState('');
  const [runFilter, setRunFilter] = useState('all');
  const latestRunCount = opportunities.filter((opportunity) => isLatestRunPresentation(opportunity.runPresentation)).length;
  const visibleOpportunities = sortByRunPresentationThenDate(opportunities.filter((opportunity) => {
    const query = search.trim().toLowerCase();
    const runMatch = runFilter !== 'latest' || isLatestRunPresentation(opportunity.runPresentation);
    if (!runMatch) return false;
    if (!matchesTrendChannel(opportunity, channel)) return false;
    if (!query) return true;
    return [
      opportunity.title,
      opportunity.summary,
      opportunity.whyRelevant,
      opportunity.dvhAngle,
      opportunity.foodserviceAngle,
      ...(opportunity.suggestedActions || []),
    ].join(' ').toLowerCase().includes(query);
  }));

  return (
    <Box sx={{ bgcolor: TREND_COLORS.pageBg, minHeight: '100vh', mx: -3, mt: -3, px: { xs: 2, md: 4 }, py: { xs: 3, md: 5 } }}>
      <Stack spacing={3} sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Stack spacing={1}>
          <Typography sx={PAGE_HEADING_SX}>MÖJLIGHETER</Typography>
          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, fontStyle: 'italic' }}>
            Matchningar och gapanalys som pekar ut nästa bästa åtgärd.
          </Typography>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            label="Sök möjligheter"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Sök på kanal, åtgärd, produkt eller ämne"
            sx={{ maxWidth: 420 }}
          />
          <TextField
            select
            label="Körning"
            value={runFilter}
            onChange={(event) => setRunFilter(event.target.value)}
            sx={{ minWidth: 190 }}
          >
            <MenuItem value="all">Alla körningar</MenuItem>
            <MenuItem value="latest">Senaste körning</MenuItem>
          </TextField>
          <TrendChannelFilter minWidth={170} />
          <Chip size="small" label={`${latestRunCount} från senaste körningen`} sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }} />
        </Stack>

        {loading && <TrendCardSkeletonGrid count={6} />}
        {error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && (
          <Grid container spacing={3}>
            {visibleOpportunities.length === 0 && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="info">Inga möjligheter matchar sökningen. Prova ett bredare ord eller kör en ny trendkörning.</Alert>
              </Grid>
            )}
            {visibleOpportunities.map((opportunity) => (
              <Grid key={opportunity._id} size={{ xs: 12, sm: 6, lg: 4 }}>
                <TrendOpportunityCard opportunity={opportunity} onOpen={(entry) => setSelectedOpportunity(entry)} />
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>
      <TrendOpportunityDetailDialog
        opportunity={selectedOpportunity}
        open={!!selectedOpportunity}
        onClose={() => setSelectedOpportunity(null)}
      />
    </Box>
  );
}
